import Tippy from '@tippyjs/react';
import { differenceInDays, format } from 'date-fns';

import { UserAvatar } from 'components/user-avatar';
import { useAuth } from 'context/AuthContext';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { FaHeart } from 'react-icons/fa';
import { MdModeComment } from 'react-icons/md';
import { TbAlertTriangleFilled, TbMessageCancel, TbSearch } from 'react-icons/tb';
import { FiEdit3 } from 'react-icons/fi';

import { registerInteraction } from 'services/interactions.service';
import { useModal } from 'context/ModalContext';
import { getKPIHistoryPag, getServerTime } from 'services/dashboard.service';
import { convertEmojis } from 'utils/helpers';
import Humanograma from 'components/Humanograma';
import ModalReportRecognition from 'components/modal-report-recognition';
import { IconLeafSeed, IconSprout } from 'components/rebrand/icons';
import { dimensaoByBehavior, hexToRgba, nivelByMoeda } from 'components/rebrand/mappings';

interface Props {
  filter?: any;
}

export default function RecognitionReceived(data: Props) {
  const { filter } = data;
  const { user, currentCycle } = useAuth();
  const [list, setList] = useState<any[]>([]);
  const { showModal } = useModal();
  const [loadingList, setLoadingList] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [load, setLoad] = useState(false);
  const searchRef = useRef<any>();
  const [currentFilter, setCurrentFilter] = useState({});
  const [time, setTime] = useState<number>();
  const [reportRecognitionId, setReportRecognitionId] = useState<string | undefined>();

  const [openCommentId, setOpenCommentId] = useState<string | undefined>();
  const [commentDraft, setCommentDraft] = useState('');
  const [loadingInteraction, setLoadingInteraction] = useState<boolean | undefined>(false);

  useEffect(() => {
    setCurrentFilter(filter);
  }, [filter]);

  useEffect(() => {
    async function loadTime() {
      const res = await getServerTime();
      setTime(res);
    }

    if (!!user && !!currentCycle) {
      loadTime();
    }
  }, [currentCycle, user]);

  const hasLike = useCallback(
    (item: any) => {
      return !!item?.interaction?.find((i: any) => i.sender === user?._id && !!i.like);
    },
    [user?._id],
  );

  function likeCount(item: any) {
    return (item?.interaction || []).filter((i: any) => !!i.like).length;
  }

  function canEdit(item: any) {
    if (!!item && !!time) {
      const differenceTime = differenceInDays(new Date(time), new Date(item.created));
      return differenceTime <= 10;
    }
    return false;
  }

  /* O aggregate já esconde reconhecimento com denúncia aberta ou procedente,
     então o que chega aqui ou nunca foi denunciado, ou teve a denúncia
     reprovada. Em qualquer caso: sem denúncia registrada, pode denunciar. */
  function canReport(item: any) {
    return !item.cancelled && !item.denuncia_status;
  }

  const sendInteraction = useCallback(
    async (item: any, type: string, message?: string) => {
      setLoadingInteraction(true);
      const index = list.findIndex((i: any) => i._id === item._id);

      let payload: any;
      const newList = [...list];
      const interactionItem = item.interaction?.[0];

      if (!item?.interaction?.length && item?.action_id) {
        payload = {
          item: item.action_id as string,
          player: item.sender as string,
          sender: user?._id as string,
          senderName: user?.name as string,
          like: type === 'like' && hasLike(item) ? false : type === 'like' && !hasLike(item) ? true : hasLike(item),
          message: type === 'message' && typeof message === 'string' && message.trim().length ? message.trim() : undefined,
          created: {
            $date: new Date().getTime(),
          },
        };
      } else if (!!item?.interaction?.length && item?.action_id && !!interactionItem?._id) {
        if (type === 'like') {
          payload = {
            ...interactionItem,
            like: !hasLike(item),
            updated: {
              $date: new Date().getTime(),
            },
          };
        } else if (type === 'message') {
          payload = {
            ...interactionItem,
            message,
            updated: {
              $date: new Date().getTime(),
            },
          };
        }

        if (typeof payload?.created === 'number') {
          payload.created = {
            $date: payload.created,
          };
        }
      }

      if (!!payload && !!payload.created) {
        const data = await registerInteraction(payload);

        if (!!newList[index]?.interaction?.length) {
          newList[index].interaction[0] = data;
        } else {
          newList[index].interaction = [data];
        }

        setList(newList);
      }

      setLoadingInteraction(false);
    },
    [hasLike, list, user?._id, user?.name],
  );

  function getComment(item: any) {
    const interaction = item.interaction?.find((e: any) => !!e.message?.trim().length);
    return interaction || null;
  }

  function handleDetail(item: any) {
    showModal('extrato-detalhe', item);
  }

  function getReturnComment(item: any) {
    if (!!item?.cancelled && !!item?.returnRecognition?.message) {
      return item?.returnRecognition?.message;
    }
    return null;
  }

  const isOldVersion = (item: any): boolean => {
    return !!item.proximidade && !!item.hierarquia && !!item.total;
  };

  function toggleComment(item: any) {
    if (openCommentId === item._id) {
      setOpenCommentId(undefined);
      setCommentDraft('');
    } else {
      setOpenCommentId(item._id);
      setCommentDraft(getComment(item)?.message || '');
    }
  }

  async function submitComment(item: any) {
    const text = commentDraft.trim();
    if (!text || loadingInteraction) return;
    await sendInteraction(item, 'message', text);
    setOpenCommentId(undefined);
    setCommentDraft('');
  }

  useEffect(() => {
    async function loadData() {
      setLoadingList(true);
      const rangeItem = page ? page * 5 : 0;

      if (page) setLoad(true);

      const data = await getKPIHistoryPag(
        user?._id,
        currentFilter,
        rangeItem,
        5,
        currentCycle?.date_start,
        currentCycle?.date_finish,
      );
      setList(page ? [...list, ...data.result] : data.result);
      setTotal(data.range.total);
      setTotalPage(data.range.pages);

      setLoadingList(false);
      setLoad(false);
    }

    if (!!user && !!currentCycle) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCycle, user, page, currentFilter]);

  const onSearch = (e: FormEvent) => {
    setCurrentFilter({ ...currentFilter, ...{ search: searchRef.current.value } });
    e.preventDefault();
  };

  return (
    <>
      <div className="rb-feed-head">
        <h3 tabIndex={0}>
          Reconhecimentos recebidos {total ? <span>({total})</span> : null}
        </h3>
        <form className="rb-feed-search" onSubmit={onSearch}>
          <span className="rb-feed-search__icon">
            <TbSearch size={16} />
          </span>
          <input
            className="rb-input rb-input--search"
            placeholder="Nome, matrícula, prefixo ou grupo"
            ref={(ref: any) => (searchRef.current = ref)}
            inputMode="search"
          />
        </form>
      </div>

      {loadingList && (
        <div className="rb-feed-grid">
          <div className="rb-skeleton" style={{ height: 220 }} />
          <div className="rb-skeleton" style={{ height: 220 }} />
        </div>
      )}

      {!loadingList && !list?.length && <div className="rb-empty">Você ainda não recebeu reconhecimentos neste ciclo.</div>}

      {!loadingList && !!list?.length && (
        <>
          <div className="rb-feed-grid">
            {list.map((item: any) => {
              const dim = dimensaoByBehavior(item.behavior);
              const nivel = nivelByMoeda(item.coin);
              const comment = getComment(item);
              return (
                <article
                  className={`rb-feeditem ${item.cancelled ? 'is-muted' : ''}`}
                  style={{ ['--acc' as any]: item.cancelled ? 'var(--gray-250)' : dim.color }}
                  key={item?._id}
                >
                  <div className="rb-feeditem__head">
                    <div className="rb-feeditem__avatar">
                      <UserAvatar data={{ ...item.sender_avatar, ariaHidden: true }} responsive={true} />
                    </div>
                    <div className="rb-feeditem__who">
                      <Humanograma data={{ ...item, nome: item.sender_name, funcao: item.sender_funcao, _id: item.sender }}>
                        <span className="rb-feeditem__name">
                          <b>{item.sender_nome_guerra || item.sender_name?.split(' ')[0]}</b>{' '}
                          <span className="rb-feeditem__key">({item.sender})</span> reconheceu você
                        </span>
                      </Humanograma>
                      {!!(item.sender_desc_unidade || item.sender_funcao) && (
                        <div className="rb-feeditem__unit">{item.sender_desc_unidade || item.sender_funcao}</div>
                      )}
                    </div>
                    <span className="rb-feeditem__date">{format(new Date(item.time), 'dd/MM - HH:mm')}</span>
                  </div>

                  {item.cancelled ? (
                    <div className="rb-feeditem__chips">
                      <span className="rb-chip rb-chip--danger">
                        <TbMessageCancel size={14} /> Reconhecimento cancelado pelo autor
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="rb-feeditem__chips">
                        <span className="rb-nivelchip">
                          <IconSprout color="#2fa457" size={13} /> {nivel.tag}
                        </span>
                        <span
                          className="rb-dimchip"
                          style={{ color: dim.color, background: hexToRgba(dim.color, 0.1), boxShadow: `inset 0 0 0 1.5px ${dim.color}` }}
                        >
                          <span className="rb-dimchip__dot" style={{ background: dim.color }} />
                          {dim.label}
                        </span>
                        <span className="rb-sementeschip" onClick={() => (isOldVersion(item) ? handleDetail(item) : null)} style={isOldVersion(item) ? { cursor: 'pointer' } : undefined}>
                          <IconLeafSeed color="var(--blue-primary)" size={13} /> {nivel.selos} selo{nivel.selos > 1 ? 's' : ''}
                        </span>
                      </div>

                      {!!item.message && (
                        <>
                          <div className="rb-feeditem__label" style={{ color: dim.color }}>
                            Reconhecimento recebido:
                          </div>
                          <p className="rb-feeditem__message" dangerouslySetInnerHTML={{ __html: convertEmojis(item.message) }} />
                        </>
                      )}

                      {!!comment && (
                        <div className="rb-feeditem__comment">
                          <div className="rb-feeditem__comment-head">
                            <span>Seu comentário</span>
                            {canEdit(comment) && (
                              <button className="rb-feeditem__edit" onClick={() => toggleComment(item)} disabled={loadingInteraction}>
                                <FiEdit3 size={14} /> Editar
                              </button>
                            )}
                          </div>
                          <div dangerouslySetInnerHTML={{ __html: convertEmojis(comment.message) }} />
                        </div>
                      )}

                      <div className="rb-feeditem__footer">
                        <button
                          className={`rb-social ${hasLike(item) ? 'is-liked' : ''}`}
                          onClick={() => {
                            if (loadingInteraction) return;
                            sendInteraction(item, 'like');
                          }}
                          disabled={loadingInteraction}
                        >
                          <FaHeart size={15} /> {likeCount(item)} Curtir
                        </button>
                        {!comment && (
                          <button className="rb-social" onClick={() => toggleComment(item)} disabled={loadingInteraction}>
                            <MdModeComment size={16} /> Comentar
                          </button>
                        )}
                        {/* Denunciar fica por último no DOM de propósito: o margin-left:auto
                            da .rb-social--icon empurra o próprio botão e todos os seguintes,
                            então qualquer item depois dele iria junto para a direita. */}
                        {canReport(item) && (
                          <Tippy content="Sinalizar reconhecimento">
                            <button
                              className="rb-social rb-social--icon"
                              aria-label="Sinalizar reconhecimento"
                              onClick={() => setReportRecognitionId(item.action_id)}
                            >
                              <TbAlertTriangleFilled size={16} />
                            </button>
                          </Tippy>
                        )}
                      </div>

                      {openCommentId === item._id && (
                        <div className="rb-feeditem__commentbox">
                          <input
                            className="rb-input"
                            value={commentDraft}
                            autoFocus
                            onChange={e => setCommentDraft(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') submitComment(item);
                            }}
                            placeholder="Escreva um comentário..."
                          />
                          <button type="button" className="rb-btn-primary" disabled={loadingInteraction} onClick={() => submitComment(item)}>
                            Enviar
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {!!getReturnComment(item) && (
                    <div className="rb-feeditem__comment">
                      <div className="rb-feeditem__comment-head">
                        <span>Motivo do cancelamento</span>
                      </div>
                      <div dangerouslySetInnerHTML={{ __html: convertEmojis(getReturnComment(item)) }} />
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          {page + 1 < totalPage && (
            <div className="rb-feed-loadmore">
              <button type="button" className="rb-btn-primary" disabled={load} onClick={() => setPage(page + 1)}>
                {load ? 'Carregando...' : 'Carregar mais'}
              </button>
            </div>
          )}
        </>
      )}

      {!!reportRecognitionId && (
        <ModalReportRecognition
          id={reportRecognitionId}
          callback={action => {
            if (action === 'reported' || action === 'rejected') {
              /* Nada de costurar o array local: a lista é paginada por offset
                 contra o servidor, e o extrato já escondeu este reconhecimento
                 (denúncia aceita) ou já o escondia antes (denúncia recusada por
                 "já existe denúncia"). Recarregar do zero, como o callback de
                 devolução acima já faz, evita o deslizamento de offset. */
              setPage(0);
              setCurrentFilter({ ...currentFilter });
            } else if (action === 'close') {
              setReportRecognitionId(undefined);
            }
          }}
        />
      )}
    </>
  );
}
